import React, { useState, useEffect } from 'react'
import { Box, Button, Card, Typography, Table, Sheet, Alert, FormControl, FormLabel, Input, Grid } from '@mui/joy'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { invoiceReportsApi } from './api'
import { DbBrokerInvoice } from '@numeo/types-core'
import dayjs from 'dayjs'

const InvoiceReportsPage: React.FC = () => {
    const [fromDate, setFromDate] = useState<string>('')
    const [toDate, setToDate] = useState<string>('')
    const [loading, setLoading] = useState(false)
    const [invoices, setInvoices] = useState<DbBrokerInvoice[]>([])
    const [error, setError] = useState<string | null>(null)

    // Set default date range (last 30 days) on component mount
    useEffect(() => {
        const today = new Date()
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(today.getDate() - 30)

        setToDate(today.toISOString().split('T')[0])
        setFromDate(thirtyDaysAgo.toISOString().split('T')[0])
    }, [])

    const handleFetchReports = async () => {
        if (!fromDate || !toDate) {
            setError('Please select both from and to dates')
            return
        }

        setLoading(true)
        setError(null)
        try {
            // Convert dates to ISO format for the API
            const fromDateISO = new Date(fromDate + 'T00:00:00Z').toISOString()
            const toDateISO = new Date(toDate + 'T23:59:59Z').toISOString()

            const response = await invoiceReportsApi.getInvoiceReports({
                fromDate: fromDateISO,
                toDate: toDateISO,
            })

            if (response.success) {
                setInvoices(response.invoices)
            } else {
                setError(response.error || 'Failed to fetch invoice reports')
            }
        } catch (err) {
            setError(err instanceof Error ? err.message || 'An unexpected error occurred' : 'An unexpected error occurred')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography level="h2" sx={{ mb: 1 }}>
                Invoice Reports
            </Typography>
            <Typography level="body-md" sx={{ mb: 3, color: 'neutral.600' }}>
                View and track broker invoices across projects within a specific date range
            </Typography>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                    <Grid xs={12} sm={4}>
                        <FormControl>
                            <FormLabel>From Date</FormLabel>
                            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} startDecorator={<CalendarTodayIcon />} />
                        </FormControl>
                    </Grid>
                    <Grid xs={12} sm={4}>
                        <FormControl>
                            <FormLabel>To Date</FormLabel>
                            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} startDecorator={<CalendarTodayIcon />} />
                        </FormControl>
                    </Grid>
                    <Grid xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button variant="solid" color="primary" fullWidth onClick={handleFetchReports} loading={loading}>
                            Generate Report
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {invoices.length > 0 ? (
                <Sheet
                    variant="outlined"
                    sx={{
                        borderRadius: 'sm',
                        overflow: 'auto',
                        maxHeight: '600px',
                    }}
                >
                    <Table stickyHeader hoverRow>
                        <thead>
                            <tr>
                                <th style={{ width: 150 }}>Invoice #</th>
                                <th>Date</th>
                                <th>Project</th>
                                <th>Bill To</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Total Due</th>
                                <th>Paid At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((invoice) => (
                                <tr key={invoice._id.toString()}>
                                    <td>{invoice.invoiceNumber}</td>
                                    <td>{dayjs(invoice.invoiceDate).format('MM/DD/YYYY')}</td>
                                    <td>{invoice.projectName}</td>
                                    <td>{invoice.billTo?.companyName}</td>
                                    <td>{invoice.status}</td>
                                    <td style={{ textAlign: 'right' }}>${invoice.totals?.totalDue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td>{invoice.paidAt ? dayjs(invoice.paidAt).format('MM/DD/YYYY') : '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            ) : (
                !loading &&
                !error && (
                    <Alert color="primary" variant="soft">
                        No invoices found for the selected date range. Click "Generate Report" to fetch data.
                    </Alert>
                )
            )}
        </Box>
    )
}

export default InvoiceReportsPage
